/**
 * i18n Helper for non-React contexts
 * Used in utilities like apiClient where React hooks are not available
 */

import { logger } from './logger';

// Cache for translations
let cachedTranslations: any = null;
let cachedLocale: string | null = null;

/**
 * Get current locale from Redux store, localStorage, or browser
 */
export function getCurrentLocale(): string {
  if (typeof window === 'undefined') {
    return 'en-US'; // Server-side default
  }

  // Try to get from Redux store first (most reliable)
  try {
    // Access Redux store directly
    const storeModule = require('../store');
    const store = storeModule.default;
    const state = store.getState();
    const reduxLanguage = state?.language?.currentLanguage;
    
    if (reduxLanguage) {
      return reduxLanguage;
    }
  } catch (e) {
    // Redux store not available or not initialized
  }

  // Try to get from localStorage (guest user preference)
  try {
    const guestLanguage = localStorage.getItem('smartmatrix_guest_language');
    if (guestLanguage) {
      return guestLanguage;
    }
  } catch (e) {
    // localStorage not available
  }

  // Try to get from browser language (map to BCP 47 format)
  const browserLang = navigator.language;
  const supportedLocales = ['en-US', 'zh-CN', 'zh-TW', 'ja-JP', 'es-ES', 'fr-FR'];
  
  // Try exact match first
  if (supportedLocales.includes(browserLang)) {
    return browserLang;
  }
  
  // Try language-only match (e.g., 'en' -> 'en-US')
  const langOnly = browserLang.split('-')[0];
  const localeMap: Record<string, string> = {
    'en': 'en-US',
    'zh': 'zh-CN',
    'ja': 'ja-JP',
    'es': 'es-ES',
    'fr': 'fr-FR',
  };
  
  return localeMap[langOnly] || 'en-US';
}

/**
 * Load translations for current locale
 */
export async function loadTranslations(locale?: string): Promise<any> {
  const currentLocale = locale || getCurrentLocale();

  // Return cached if same locale
  if (cachedTranslations && cachedLocale === currentLocale) {
    return cachedTranslations;
  }

  try {
    const translations = await import(`../../locales/${currentLocale}.json`);
    cachedTranslations = translations.default;
    cachedLocale = currentLocale;
    return cachedTranslations;
  } catch (error) {
    logger.error('Failed to load translations for locale', error, { currentLocale });
    // Fallback to English (US)
    const fallback = await import(`../../locales/en-US.json`);
    cachedTranslations = fallback.default;
    cachedLocale = 'en-US';
    return cachedTranslations;
  }
}

/**
 * Get translated message
 * @param key - Translation key (e.g., 'errors.api.networkFailed')
 * @param params - Optional parameters for interpolation
 */
export function translate(key: string, params?: Record<string, any>): string {
  const currentLocale = getCurrentLocale();
  
  // Reload translations if locale changed
  if (!cachedTranslations || cachedLocale !== currentLocale) {
    try {
      cachedTranslations = require(`../../locales/${currentLocale}.json`);
      cachedLocale = currentLocale;
    } catch (error) {
      cachedTranslations = require(`../../locales/en-US.json`);
      cachedLocale = 'en-US';
    }
  }

  // Navigate through nested keys
  const keys = key.split('.');
  let value: any = cachedTranslations;

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      logger.warn('Translation key not found', { key });
      return key; // Return key if translation not found
    }
  }

  // Handle interpolation
  if (typeof value === 'string' && params) {
    return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
      return params[paramKey] !== undefined ? String(params[paramKey]) : match;
    });
  }

  return typeof value === 'string' ? value : key;
}

/**
 * Initialize translations (call this early in app lifecycle)
 */
export async function initializeTranslations(): Promise<void> {
  await loadTranslations();
}
